<template>
  <div v-if="taskType === 'browsecsv'" class="modalbody">
    <div class="labeldiv" v-if="taskType === 'browsecsv'">
      <label class="font-regular-14 breaking-words mb-3">
        <strong>CSV File Instructions:</strong><br />
        <ul style="margin: 0 0 0 1.2em; padding: 0">
          <li>Separate values using comma , or semicolon ;</li>
          <li>Include a header row: FirstName,LastName,Email</li>
        </ul>
        <div style="margin-top: 0.5em"><b>Example:</b></div>
        <pre class="csvexample">
          FirstName,LastName,Email
          John,Doe,john.doe@domain.com
          Mike,Smith,mike.smith@domain.com
        </pre>
      </label>
      <el-upload
        ref="uploadRef"
        :before-upload="beforeUpload"
        :show-file-list="false"
        accept=".csv"
        :limit="1"
        class="w-full mb-3"
      >
        <template #trigger>
          <el-button
            round
            class="csvbuttonstyle"
            variant="warning"
            id="uploadcsvfile"
            >Upload CSV file</el-button
          >
        </template>
      </el-upload>
      <el-input
        v-model="choosedFilepath"
        placeholder="No file selected"
        readonly
        class="user-inputstyle"
      >
        <template #prefix>
          <el-icon :size="25"><document /></el-icon>
        </template>
      </el-input>
      <div v-if="showCSVErrors">
        <label class="error-label font-bold-16">
          Please verify the below errors in the CSV file:</label
        >
        <div class="validationErrors">
          <div
            v-for="(item, index) in csvErrors"
            :key="index"
            class="font-regular-16 btm-1"
          >
            <div class="datadisplaydiv">
              {{ item }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="buttonsdiv">
      <el-button
        id="adduserbutton"
        round
        class="btnstyle"
        variant="warning"
        @click="nextCSVFileHandler"
        :disabled="selectedCSVFile === undefined || showCSVErrors"
      >
        Next
      </el-button>
      <el-button
        id="addcompanyuserbutton"
        round
        class="btnstyle"
        variant="warning"
        @click="goBacktoAddSingleUser"
      >
        Back
      </el-button>
      <el-button
        id="usercancel"
        round
        variant="outline-dark"
        @click="cancelInvite"
        class="cancelbtn"
      >
        Cancel
      </el-button>
    </div>
  </div>
  <div v-if="taskType === 'previewcsv'" class="modalbody" v-loading="loading">
    <div class="labeldiv">
      <label class="font-bold-18 btm-2">Preview users from CSV file </label>
      <label class="font-regular-18 mb-3"
        ><strong>{{ csvRowsCount }}</strong> users will be added and below is
        the preview of first 10 users for your reference
      </label>

      <div class="userlistdiv">
        <div v-for="(user, index) in previewUsers" :key="index">
          <label class="font-regular-16 breaking-words">
            {{ user.firstname }}&#44; {{ user.lastname }}&#44;
            {{ user.email }}
          </label>
        </div>
      </div>
    </div>

    <div class="buttonsdiv">
      <el-button
        id="adduserbutton"
        round
        class="btnstyle"
        variant="warning"
        @click="uploadCSVFileUsers"
      >
        Send Invite
      </el-button>
      <el-button
        id="addcompanyuserbutton"
        round
        class="btnstyle"
        variant="warning"
        @click="browseCSVFileHandler"
      >
        Back
      </el-button>
      <el-button
        id="usercancel"
        round
        variant="outline-dark"
        @click="cancelInvite"
        class="cancelbtn"
      >
        Cancel
      </el-button>
    </div>
  </div>
  <div v-if="taskType === 'csvResponseReceived'" class="modalbody">
    <div class="innerModal" v-if="csvResponseReceived">
      <img
        src="@/assets/icons/mailsent.svg"
        style="width: 70px; margin-bottom: 1rem"
        alt="mailsentimg"
        title="mailsentimg"
      />
      <label class="labelbld"
        >Your file is currently being processed. You will receive an email
        notification once it’s complete. After that, please refresh the user
        list to see new users.
      </label>
    </div>
    <div class="buttonsdiv">
      <el-button
        id="adduserdone"
        round
        class="btnstyle"
        variant="warning"
        @click="cancelInvite"
        style="float: right"
      >
        Done
      </el-button>
    </div>
  </div>
</template>

<style lang="scss">
.csvexample {
  background: #f8f8f8;
  border-radius: 4px;
  padding: 0.5em;
  margin: 0 0 0 0;
  font-size: 13px;
  white-space: pre-line;
}
.validationErrors {
  max-height: 30vh;
  overflow-y: auto;
  flex-direction: column;
}
.error-label {
  display: block;
  margin-top: 1em;
  margin-bottom: 1em;
  width: 100%;
  color: #df444d;
}
</style>

<script lang="ts">
import { defineComponent, ref, PropType } from "vue";
import { mapGetters } from "vuex";
import APIService from "@/utils/APIService";
import { FlowActionTypes } from "@/store/flowstore/FlowActionTypes";
import type { UploadUserFile } from "element-plus"; //UploadProps
import Papa from "papaparse";
import { ElMessage } from "element-plus";

interface CsvRow {
  firstname: string;
  lastname: string;
  email: string;
}

export default defineComponent({
  name: "AddCSVFileModal",
  components: {},
  computed: {
    ...mapGetters([
      "getAllCompanies",
      "getTaskType",
      "getCompanyData",
      "getCurrentUser",
    ]),
    taskType(): string {
      return this.getTaskType;
    },
    company(): any {
      return this.companyData;
    },
  },
  data() {
    return {
      enableControls: true,
      showError: false,
      loading: false,
      addUserError: "",
      noemail: false,
      hasError: false,
      fileList: ref<UploadUserFile[]>([]),
      requiredHeaders: ["firstname", "lastname", "email"],
      previewUsers: [] as CsvRow[],
      choosedFilepath: ref<string>(""),
      selectedCSVFile: ref<File | null>(),
      csvErrors: [] as string[],
      csvRowsCount: 0,
      showCSVErrors: false,
      csvResponseReceived: false,
      csvResponse: {} as any,
    };
  },
  watch: {},
  methods: {
    goBacktoAddSingleUser() {
      this.$store.dispatch(FlowActionTypes.SET_TASK_TYPE, "add");
    },
    browseCSVFileHandler() {
      this.$store.dispatch(FlowActionTypes.SET_TASK_TYPE, "browsecsv");
    },
    nextCSVFileHandler() {
      this.$store.dispatch(FlowActionTypes.SET_TASK_TYPE, "previewcsv");
    },
    beforeUpload(file: File) {
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCSV) {
        ElMessage.error("Please upload files in CSV format only!");
        return false;
      }
      this.choosedFilepath = file.name;
      this.selectedCSVFile = file;
      this.csvErrors = [];
      this.showCSVErrors = false;
      this.parseCsv(this.selectedCSVFile);
      return false;
    },
    parseCsv(file: File) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim().toLowerCase(),
        transform: (value) => value.trim(),
        complete: (results) => {
          const rows = results.data as CsvRow[];
          // Normalize keys for duplicate detection
          const normalizedRowsForDup = rows.map((row) =>
            Object.fromEntries(
              Object.entries(row).map(([k, v]) => [k.toLowerCase(), v])
            )
          );
          const duplicateUserRows = normalizedRowsForDup.filter(
            (row, index, arr) =>
              arr.findIndex((r) => r.email === row.email) !== index
          );
          if (rows.length < 1) {
            this.csvErrors.push(
              `Please upload a CSV file containing at least one record.`
            );
          }
          if (rows.length > 200) {
            this.csvErrors.push(
              `Please upload CSV file containing 200 records or less.`
            );
          }
          if (duplicateUserRows.length > 0) {
            const emails = duplicateUserRows.map((row) => row.email);
            const uniqueEmails = Array.from(new Set(emails));
            const emailsString = uniqueEmails.join(", ");

            this.csvErrors.push(
              `Please remove the following duplicate emails from the CSV file: ${emailsString}.`
            );
          }
          this.csvRowsCount = rows.length;
          const headers = (results.meta.fields ?? []).map((h) =>
            h.trim().toLowerCase()
          );
          const normalizedRows = rows.map((row) =>
            Object.fromEntries(
              Object.entries(row).map(([k, v]) => [k.toLowerCase(), v])
            )
          );
          for (const req of this.requiredHeaders) {
            if (!headers.includes(req.toLowerCase())) {
              this.csvErrors.push(`Missing required header: ${req}.`);
            }
          }
          this.previewUsers = rows.slice(0, 10);

          normalizedRows.forEach((row, index) => {
            if (!row.firstname || !row.lastname || !row.email) {
              this.csvErrors.push(
                `Row ${
                  index + 1
                }: Number of columns does not match number of columns in the header row.`
              );
            } else {
              if (row.firstname?.length > 60 || row.lastname?.length > 60) {
                this.csvErrors.push(
                  `Row ${
                    index + 1
                  }: first and last name values can be a maximum of 60 characters.`
                );
              }
              if (
                !/^[\p{L}\p{M}][\p{L}\p{M}' -]*$/u.test(row.firstname) ||
                !/^[\p{L}\p{M}][\p{L}\p{M}' -]*$/u.test(row.lastname)
              ) {
                this.csvErrors.push(
                  `Row ${index + 1}: name value contains invalid characters.`
                );
              }
              if (
                row.email &&
                !/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9._+-]+\.[a-zA-Z-]{1,24}$/.test(
                  row.email
                )
              ) {
                this.csvErrors.push(
                  `Row ${index + 1}: contains invalid email ${row.email}.`
                );
              }
            }
          });

          if (this.csvErrors.length > 0) {
            this.showCSVErrors = true;
            return;
          }
        },
        error: (err) => {
          ElMessage.error("Error parsing CSV: " + err.message);
        },
      });
    },
    uploadCSVFileUsers() {
      if (!this.selectedCSVFile) {
        ElMessage.warning("Please select a CSV file to upload.");
        return;
      }
      const apiService = new APIService(this.axios);
      const csvFiletoUpload = new FormData();
      csvFiletoUpload.append(
        "companyBucketId",
        this.company !== null ? this.company.companyBucketId : null
      );

      csvFiletoUpload.append(
        "file",
        this.selectedCSVFile,
        this.selectedCSVFile.name
      );
      this.enableControls = false;
      this.loading = true;

      apiService
        .addMultipleUsersCSV(csvFiletoUpload)
        .then((res) => {
          if (res?.success === true) {
            this.csvResponseReceived = true;
            this.csvResponse = res;
            this.$store.dispatch(
              FlowActionTypes.SET_TASK_TYPE,
              "csvResponseReceived"
            );
            this.loading = false;
            this.enableControls = true;
          } else {
            ElMessage.error("Upload failed: Server Error ");
          }
        })
        .catch((error) => {
          ElMessage.error(
            "Upload failed: " +
              (error?.response?.data?.ValidationResult?.ValidationMessages ||
                error?.message)
          );
        });
    },
  },
  props: {
    cancelInvite: {
      type: Function as PropType<() => void>,
      required: false,
    },
    companyData: {
      type: Object as PropType<any>,
      required: false,
    },
  },
});
</script>
